import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import sortBy from 'lodash/sortBy';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  getManagementOrganizations,
  getManagementUsers,
  createManagementOrg,
  getManagementStats,
} from '../../../../store/management';
import { toastr } from '../../../../components/CustomToast';

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, subvalue, valueColor }) {
  return (
    <Card variant='outlined' sx={{ flex: 1, minWidth: 130 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontWeight: 600,
            fontSize: 10,
            display: 'block',
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant='h5'
          fontWeight={700}
          color={valueColor ?? 'text.primary'}
          sx={{ lineHeight: 1 }}
        >
          {value}
        </Typography>
        {subvalue && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ mt: 0.5, display: 'block' }}
          >
            {subvalue}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ─── CreateManagementOrgDialog ────────────────────────────────────────────────

function CreateManagementOrgDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await dispatch(createManagementOrg(name.trim())).unwrap();
      toastr.success({ title: `Org "${name.trim()}" created` });
      setName('');
      onClose();
    } catch {
      toastr.error({ title: 'Error', message: 'Failed to create org' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Create EventSync Organization</DialogTitle>
      <DialogContent sx={{ pt: 2, mt: 0 }}>
        <TextField
          autoFocus
          size='small'
          label='Organization Name'
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={!name.trim() || saving}
        >
          {saving ? <CircularProgress size={18} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Console ──────────────────────────────────────────────────────────────────

export default function Console() {
  const dispatch = useDispatch();
  const { organizations, users, stats } = useSelector(
    (state) => state.management
  );
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    dispatch(getManagementOrganizations());
    dispatch(getManagementUsers());
    dispatch(getManagementStats());
  }, [dispatch]);

  const rows = useMemo(
    () => sortBy(organizations, (o) => o.name ?? ''),
    [organizations]
  );

  return (
    <Box sx={{ maxWidth: 900, width: '100%', px: 1, mt: 1, mb: 5 }}>
      {/* ── KPI row ──────────────────────────────────────────────────── */}
      <Stack direction='row' spacing={1.5} sx={{ mb: 3 }} flexWrap='wrap'>
        <StatCard label='Organizations' value={organizations.length} />
        <StatCard label='Users' value={users.length} />
        <StatCard label='Connected Buildings' value={stats.total_sites} />
        <StatCard
          label='Total Sq Ft'
          value={
            stats.total_area != null
              ? stats.total_area.toLocaleString()
              : undefined
          }
        />
      </Stack>

      {/* ── Orgs table ───────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant='subtitle1' fontWeight={600}>
          Organizations
        </Typography>
        <Button
          size='small'
          variant='contained'
          startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}
          onClick={() => setCreateDialogOpen(true)}
        >
          New Org
        </Button>
      </Box>
      <Card variant='outlined'>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Organization</TableCell>
              <TableCell>ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((org) => (
              <TableRow key={org.org_id} hover>
                <TableCell>
                  <Typography variant='body2' fontWeight={500}>
                    {org.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='caption' color='text.secondary'>
                    {org.org_id}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align='center'>
                  <Typography variant='body2' color='text.secondary'>
                    No organizations
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <CreateManagementOrgDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </Box>
  );
}
